import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

const AUTH_COOKIE_NAME = 'r3c_auth'
const AUTH_COOKIE_MAX_AGE_DAYS = 7

function getCookieDomain(): string | null {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname
  if (host === 'localhost' || host.endsWith('.localhost')) return null
  const parts = host.split('.')
  if (parts.length < 2) return null
  return '.' + parts.slice(-2).join('.')
}

function setAuthCookie() {
  if (typeof document === 'undefined') return
  const domain = getCookieDomain()
  const expires = new Date(Date.now() + AUTH_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString()
  let cookie = `${AUTH_COOKIE_NAME}=1; path=/; expires=${expires}; secure; samesite=lax`
  if (domain) cookie += `; domain=${domain}`
  document.cookie = cookie
}

function clearAuthCookie() {
  if (typeof document === 'undefined') return
  const domain = getCookieDomain()
  let cookie = `${AUTH_COOKIE_NAME}=; path=/; expires=${new Date(0).toUTCString()}; secure; samesite=lax`
  if (domain) cookie += `; domain=${domain}`
  document.cookie = cookie
}

function hasAuthCookie(): boolean {
  if (typeof document === 'undefined') return false
  return document.cookie.split(';').some((c) => c.trim().startsWith(`${AUTH_COOKIE_NAME}=`))
}

interface User {
  id: string
  email: string
  name: string
  role: string
  tenantId: string
  twoFactorEnabled?: boolean
  marketingConsent?: boolean
}

interface Tenant {
  id: string
  name: string
  slug: string
  plan: string
  trialEndsAt?: string | null
}

interface AuthState {
  user: User | null
  tenant: Tenant | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  _hasHydrated: boolean
  setAuth: (data: { user: User; tenant: Tenant; accessToken: string; refreshToken: string }) => void
  logout: () => void
  setHasHydrated: (hasHydrated: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,
      setAuth: (data) => {
        set({
          user: data.user,
          tenant: data.tenant,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
        })
        setAuthCookie()
      },
      logout: () => {
        // Fire-and-forget: blacklist the token server-side, but never block local logout
        api.post('/auth/logout').catch(() => {})
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
        clearAuthCookie()
        // Notify the app to clear the React Query cache so the next login doesn't
        // see stale tenant data from the previous session.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('r3c:logout'))
        }
      },
      setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),
    }),
    {
      name: 'r3c-auth',
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true)
          // If the shared auth cookie is missing, treat the session as logged out.
          // This keeps the marketing site header and the dashboard in sync when
          // the user logs out from a different subdomain.
          if (!hasAuthCookie() && state.isAuthenticated) {
            state.logout()
          }
        }
      },
    }
  )
)
